/**
 * AB#1797 (CR-2026-05-29) — Realtime subscriber for `appointment.paymentChanged`
 * events from `/hubs/calendar` (the same hub used by AB#1753 calendar realtime).
 *
 * On receipt:
 *   - Invalidates the `appointmentListV2` TanStack query so the row reflects
 *     the new payment status without a manual refresh.
 *   - Fires a 4-second slide-in toast: "{customer} just paid {amount} via {method}"
 *     (suppressed for the actor's own session — actorUserProfileId === self).
 *   - Fires the `appointments.signalrPaymentEventReceived` analytics event
 *     (debug only, non-prod).
 *
 * The hook extends the existing AB#1753 connection — it does NOT create a
 * duplicate connection. It registers an additional event handler on the same
 * `HubConnection` instance via the `buildCalendarConnection` factory.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { buildCalendarConnection } from '../services/calendarRealtime';
import { queryKeys } from '../lib/queryKeys';
import { useAuthContext } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { trackEvent } from '../lib/analytics';
import { useTranslation as useTranslationUntyped } from 'react-i18next';

const useTranslation = useTranslationUntyped as any;

// AB#1797 UAT FORCE-ON (CR-2026-05-30) — was env-gated; hardcoded so the SignalR
// payment-changed subscription actually attaches during UAT. Revert with the rest of the chain.
const FLAG_ENABLED = true;

export function useAppointmentsRealtime(currentUserProfileId?: number): void {
    const qc = useQueryClient();
    const { accessToken } = useAuthContext();
    const { showToast } = useToast();
    const { t } = useTranslation('appointments');

    useEffect(() => {
        if (!FLAG_ENABLED || !accessToken) return;

        const connection = buildCalendarConnection();

        const handler = (payload: {
            appointmentId?: number;
            paymentStatus?: string;
            paymentMethod?: string;
            source?: string;
            actorUserProfileId?: number;
            actorDisplayName?: string;
            amount?: number;
            currencyIso?: string;
            customerName?: string;
        }) => {
            // Invalidate list so the row updates.
            qc.invalidateQueries({ queryKey: queryKeys.appointments.listV2() });

            // Toast only for events from OTHER actors.
            const isSelf = currentUserProfileId != null
                && payload.actorUserProfileId === currentUserProfileId;
            if (!isSelf && payload.paymentStatus === 'Paid') {
                const customer = payload.customerName ?? '—';
                const amount = payload.amount != null
                    ? `${payload.amount} ${payload.currencyIso ?? ''}`
                    : '';
                const method = payload.paymentMethod ?? '';
                showToast(
                    t('realtimeToast.paidByActor', { customer, amount, method }),
                    'success',
                );
            }

            // D1 analytics — debug non-prod only.
            if (process.env.NODE_ENV !== 'production') {
                try {
                    trackEvent('appointments.signalrPaymentEventReceived', {
                        appointmentId: payload.appointmentId,
                        paymentStatus: payload.paymentStatus,
                        source: payload.source,
                    });
                } catch {
                    // swallow
                }
            }
        };

        connection.on('appointment.paymentChanged', handler);

        const startOrReuse = async () => {
            try {
                if (connection.state === 'Disconnected') {
                    await connection.start();
                }
            } catch {
                // best-effort; hub will reconnect automatically
            }
        };
        startOrReuse();

        return () => {
            connection.off('appointment.paymentChanged', handler);
            // S1 (AB#2204, SG-11) — this hook owns the connection it built: stop it on
            // sign-out / token change so a revoked session leaves no reconnecting hub.
            void connection.stop().catch(() => undefined);
        };
    }, [accessToken, currentUserProfileId, qc, showToast, t]);
}
